import type { FsmTranslations } from "@yalana/contracts";
import type { TFunction } from "i18next";

/**
 * @description Translates a label if it's a valid i18n key, otherwise returns it as-is.
 * Useful for FSM entities that can store either a machine key or a raw user-defined string.
 */
export function translateLabel(label: string, t: TFunction): string {
  // If the label is empty, return it as-is
  if (!label) return label;

  // i18next returns the key itself if the translation is missing.
  // We can check if it exists first to avoid returning raw keys when we don't want to.
  const translated = t(label);

  // If the key resolved to a non-string (i.e. a nested namespace object), return the raw label.
  if (typeof translated !== "string") {
    return label;
  }

  // If it returned the same string and it doesn't look like a standard key path (contains dots),
  // it's likely a raw user-defined string.
  if (translated === label && !label.includes(".")) {
    return label;
  }

  return translated;
}

/**
 * @description Resolves a display label from a DB-stored FSM entity.
 * Prefers the locale-specific translation, falls back to the entity name.
 */
export function resolveLabel(
  name: string,
  translations: FsmTranslations | null | undefined,
  locale: string,
): string {
  if (!translations) return name;
  const key = locale as keyof FsmTranslations;
  const translated = translations[key];
  if (!translated) return name;
  return translated;
}
